import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { tr } from 'date-fns/locale';
import format from 'date-fns/format';
import axios from 'src/utils/axios';
import { isValid } from 'date-fns';
import { getUser } from 'src/utils/Common';
import CloseIcon from '@mui/icons-material/Close';
import Alert from 'src/components/Alert';
import PropTypes from 'prop-types';


const BeratHesaplaDialog = ({ open, onClose, onPreview }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const SessionUser = getUser();
  const [uyari, setUyari] = useState(false);
  const [uyariTip, setUyariTip] = useState('default');
  const [responseMessage, setResponseMessage] = useState('');

  const uyariGoster = (message, tip = 'error') => {
    setResponseMessage(message);
    setUyariTip(tip);
    setUyari(true);
  };

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setUyari(false);
  };

  const handleConfirmClick = () => {
    if (selectedDate) {
      setConfirmOpen(true);
    }
  };

  const handleActualConfirm = () => {
    setSubmitting(true);
    const formattedDateForSQL = format(selectedDate, 'yyyy-MM-dd');
    axios
      .post(`SetYSBeratDate?HesapTarihi=${formattedDateForSQL}`)
      .then((response) => {
        if (response.data.ErrorCode === "0") {
          setSelectedDate(new Date());
          setConfirmOpen(false);
          setSubmitting(false);
          onClose();
        } else {
          uyariGoster('İşlem sırasında bir hata oluştu.', 'error');
          setSubmitting(false);
        }
      })
      .catch(() => {
        uyariGoster('Sunucuya bağlanırken bir hata oluştu.', 'error');
        setSubmitting(false);
      });
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    onClose();
  };

  const handlePreview = () => {
    setPreviewing(true);
    const formattedDateForSQL = format(selectedDate, 'yyyy-MM-dd');
    axios.get(`GetYSBeratOnizleme?HesapTarihi=${formattedDateForSQL}`)
      .then((response) => {
        if (response.data.length > 0) {
          onPreview(response.data)
          setSelectedDate(new Date());
          setConfirmOpen(false);
          setPreviewing(false);
          onClose();
        } else {
          uyariGoster(format(selectedDate, 'dd-MM-yyyy') + " tarihine göre bir kayıt bulunamadı.", 'error');
          setPreviewing(false);
        }
      })
      .catch(() => {
        uyariGoster('Sunucuya bağlanırken bir hata oluştu.', 'error');
        setPreviewing(false);
      });
  };

  const disabledReason = !isValid(selectedDate)
    ? 'Geçerli bir tarih seçiniz.'
    : !(SessionUser.Role_Id === 1 || SessionUser.Role_Id === 3)
      ? 'Bu işlem için yetkiniz yok.'
      : '';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
      <Snackbar
        open={uyari}
        autoHideDuration={6000}
        onClose={uyariKapat}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={uyariKapat} variant={uyariTip} message={responseMessage} />
      </Snackbar>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Berat Hesaplama
          <IconButton aria-label="close" size="small" onClick={handleCancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            Berat Tarihi
          </Typography>
          <DatePicker
            value={selectedDate}
            onChange={(newDate) => setSelectedDate(newDate)}
            format="dd.MM.yyyy"
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                variant: 'outlined'
              }
            }}
          />
          {isValid(selectedDate) && (
            <Typography sx={{ mt: 2 }}>
              {format(selectedDate, 'dd.MM.yyyy')} tarihine göre berat hesaplatıyorsunuz.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="error">
            İptal
          </Button>
          <Button
            onClick={handlePreview}
            color="secondary"
            disabled={!isValid(selectedDate) || previewing}
            startIcon={previewing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Önizleme
          </Button>
          <Tooltip title={disabledReason} disableHoverListener={!disabledReason}>
            <span>
              <Button
                onClick={handleConfirmClick}
                variant="contained"
                disabled={!!disabledReason}
                style={disabledReason ? undefined : { backgroundColor: '#f57c00', color: '#fff' }}
              >
                Hesapla
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Onay Dialogu */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Onayla</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{selectedDate ? format(selectedDate, 'dd.MM.yyyy') : ''}</strong> tarihini seçtiniz.
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Bu tarih baz alınarak beratlar <strong>Onay Bekleniyor</strong> durumunda eklenecek. Emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="error" disabled={submitting}>
            İptal
          </Button>
          <Button
            onClick={handleActualConfirm}
            variant="contained"
            color="primary"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            style={{ backgroundColor: '#f57c00', color: '#fff' }}
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default BeratHesaplaDialog;

BeratHesaplaDialog.propTypes = {
  onClose: PropTypes.func,
  onPreview: PropTypes.func,
  open: PropTypes.bool
};
